// lib/services/cameraselect/camera_selector_service.ts
//
// Stage-owned state/service backing the overlaid Camera Selector UI. It lets the
// user explicitly choose a camera (overriding CaptureService's natural
// selection) BEFORE scanning, or fall back to Automatic.
//
// Decoupling: React talks ONLY to this service (snapshot/subscribe, shaped for
// useSyncExternalStore); this service talks to capture through a narrow
// CapturePort interface. It never imports the WebGPU/Stage graph, so it is
// SSR-safe, cheap to construct, and unit-testable by direct import with a fake
// port + in-memory storage.

import {
  build_camera_options,
  type CameraOption,
  type CameraDescriptor,
} from '../capture/camera_options';

// Narrow surface this service needs from CaptureService. CaptureService
// implements exactly these members, but depending on the interface (not the
// class) keeps the WebGPU graph out of the unit build and out of React's reach.
export interface CapturePort {
  // Idempotent, de-duped discovery (enumerate + probe). Safe to call repeatedly
  // and concurrently without launching duplicate probes.
  ensure_discovered(): Promise<void>;
  get_cameras(): CameraDescriptor[];
  // Exact-deviceId override applied before the natural selection. Returns true
  // if the override was applied, false if the device was not found (in which
  // case capture safely falls back to automatic).
  set_manual_override(device_id: string): boolean;
  clear_manual_override(): void;
  // Build a complete diagnostic dump (delimited console lines) for one camera
  // from data already captured during discovery. Opens no stream and never
  // throws (failures come back as a single error line).
  get_selection_dump(device_id: string): string[];
  is_capturing(): boolean;
  enumerate_state: EnumerateState;
  device_model: string | null;
  device_model_note: string | null;
}

export type EnumerateState = 'idle' | 'ok' | 'no-permission' | 'unavailable' | 'error';

export type DiscoveryStatus =
  | 'idle'
  | 'discovering'
  | 'ready'
  | 'permission-required'
  | 'empty'
  | 'error';

export interface CameraSelectorSnapshot {
  visible: boolean;
  status: DiscoveryStatus;
  error: string | null;
  notice: string | null;
  cameras: readonly CameraOption[];
  // null means "Automatic" (use CaptureService's natural selection).
  selected_device_id: string | null;
  device_model: string | null;
  device_model_note: string | null;
}

// Minimal storage surface (window.localStorage satisfies it). Injected so the
// service is SSR-safe and testable with an in-memory fake.
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface PersistedSelection {
  device_id: string;
  friendly: string;
  label: string;
  facing: string;
}

export const STORAGE_KEY = 'echo-camera-selection';

const SCAN_ACTIVE_NOTICE =
  'Scanning is active — the chosen camera will apply to the next scan.';

type Listener = () => void;

export default class CameraSelectorService {
  private readonly capture: CapturePort;
  private readonly storage: StorageLike | null;
  private readonly log: (message: string) => void;

  private visible = false;
  private status: DiscoveryStatus = 'idle';
  private error: string | null = null;
  private notice: string | null = null;
  private cameras: CameraOption[] = [];
  private selected_device_id: string | null = null;
  private device_model: string | null = null;
  private device_model_note: string | null = null;

  private discovering = false;

  private readonly listeners = new Set<Listener>();
  private snapshot: CameraSelectorSnapshot;

  constructor(
    capture: CapturePort,
    storage: StorageLike | null = null,
    log: (message: string) => void = () => {}
  ) {
    this.capture = capture;
    this.storage = storage;
    this.log = log;
    this.snapshot = this.build_snapshot();
  }

  // ---- Visibility --------------------------------------------------------

  show(): void {
    this.set_visible(true);
  }

  hide(): void {
    this.set_visible(false);
  }

  toggle(): void {
    this.set_visible(!this.visible);
  }

  is_visible(): boolean {
    return this.visible;
  }

  private set_visible(next: boolean): void {
    if (this.visible === next) return;
    this.visible = next;
    this.commit();
  }

  // ---- React store interface ---------------------------------------------

  getSnapshot = (): CameraSelectorSnapshot => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // ---- Discovery ---------------------------------------------------------

  // Trigger camera discovery. De-duped: concurrent/repeat calls while a
  // discovery is in flight are ignored (no duplicate probes). Reuses the
  // existing CaptureService enumerate/probe/permission flow.
  async discover(): Promise<void> {
    if (this.discovering) return;
    this.discovering = true;
    this.status = 'discovering';
    this.error = null;
    this.commit();

    try {
      await this.capture.ensure_discovered();
    } catch (err) {
      this.error = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    }

    const cams = this.capture.get_cameras();
    this.cameras = build_camera_options(cams);
    this.device_model = this.capture.device_model;
    this.device_model_note = this.capture.device_model_note;

    if (this.cameras.length === 0) {
      switch (this.capture.enumerate_state) {
        case 'no-permission':
          this.status = 'permission-required';
          this.error = 'Camera permission is required. Tap "Find cameras" to grant access.';
          break;
        case 'unavailable':
          this.status = 'error';
          this.error = 'This browser does not expose camera enumeration.';
          break;
        case 'error':
          this.status = 'error';
          this.error = this.error ?? 'Camera discovery failed.';
          break;
        default:
          this.status = 'empty';
          this.error = null;
      }
    } else {
      this.status = 'ready';
      this.error = null;
      this.restore_persisted();
    }

    this.discovering = false;
    this.commit();
  }

  // ---- Selection ---------------------------------------------------------

  // Choose a camera by deviceId, or pass null for Automatic. Persists the
  // explicit choice; Automatic clears persistence. If a scan is active the
  // change is accepted and applied to the NEXT scan (least-risky: the live
  // stream is not stopped/restarted mid-scan) with a clear notice.
  select(device_id: string | null): void {
    if (device_id === null) {
      this.capture.clear_manual_override();
      this.selected_device_id = null;
      this.persist_clear();
      this.error = null;
      this.notice = this.capture.is_capturing() ? SCAN_ACTIVE_NOTICE : null;
      this.log('[camera-selector] selection cleared → Automatic');
      // Automatic reverts to the engine's natural selection; a short reset
      // marker is emitted but no per-device diagnostic dump.
      this.log(
        '[camera dump] selection reset → Automatic (engine natural selection; no device dump emitted)'
      );
      this.commit();
      return;
    }

    const opt = this.cameras.find((c) => c.device_id === device_id);
    if (!opt) {
      this.error = 'That camera is no longer available; refresh discovery.';
      this.commit();
      return;
    }

    const applied = this.capture.set_manual_override(device_id);
    if (!applied) {
      // Override could not be applied — fall back safely to Automatic.
      this.capture.clear_manual_override();
      this.selected_device_id = null;
      this.persist_clear();
      this.error = `Could not select ${opt.friendly}; using Automatic instead.`;
      this.notice = null;
      this.log(`[camera-selector] override failed for ${opt.friendly}; fell back to Automatic`);
      this.commit();
      return;
    }

    this.selected_device_id = device_id;
    this.error = null;
    this.notice = this.capture.is_capturing() ? SCAN_ACTIVE_NOTICE : null;
    this.persist(opt);
    this.log(
      `[camera-selector] manual override set → ${opt.friendly} ` +
        `label="${opt.label || '(empty)'}" ${opt.detail}`
    );
    // Selection is fully applied, persisted, and committed above. Only AFTER
    // that do we emit the diagnostic dump, guarded so a serialization failure
    // logs an error but can never undo or block the selection.
    this.commit();
    this.emit_selection_dump(opt);
  }

  // Emit the full per-camera diagnostic dump to the in-app console. Never
  // throws: if the serializer/port fails, we log the error and leave the
  // already-applied selection intact (requirement: selection must still apply
  // if serialization fails).
  private emit_selection_dump(opt: CameraOption): void {
    try {
      for (const line of this.capture.get_selection_dump(opt.device_id)) {
        this.log(line);
      }
    } catch (err) {
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      this.log(
        `[camera dump error] diagnostics failed but selection applied (${opt.friendly}): ${msg}`
      );
    }
  }

  // Explicit Automatic control (alias of select(null)).
  clear(): void {
    this.select(null);
  }

  // ---- Persistence -------------------------------------------------------

  // Restore a previously persisted explicit selection, but ONLY if that exact
  // device is still present in the freshly enumerated list. Otherwise the stale
  // entry is cleared and we stay on Automatic.
  private restore_persisted(): void {
    const raw = this.storage?.getItem(STORAGE_KEY) ?? null;
    if (!raw) {
      this.selected_device_id = null;
      return;
    }

    let parsed: PersistedSelection | null = null;
    try {
      const obj = JSON.parse(raw) as unknown;
      if (obj && typeof obj === 'object' && typeof (obj as PersistedSelection).device_id === 'string') {
        parsed = obj as PersistedSelection;
      }
    } catch {
      parsed = null;
    }

    if (!parsed) {
      this.persist_clear();
      this.selected_device_id = null;
      return;
    }

    const opt = this.cameras.find((c) => c.device_id === parsed!.device_id);
    if (!opt) {
      this.log(
        `[camera-selector] persisted camera "${parsed.friendly || parsed.device_id}" ` +
          'no longer present; clearing and using Automatic'
      );
      this.persist_clear();
      this.selected_device_id = null;
      return;
    }

    const applied = this.capture.set_manual_override(opt.device_id);
    if (applied) {
      this.selected_device_id = opt.device_id;
      this.log(`[camera-selector] restored manual override → ${opt.friendly}`);
    } else {
      this.capture.clear_manual_override();
      this.persist_clear();
      this.selected_device_id = null;
    }
  }

  private persist(opt: CameraOption): void {
    if (!this.storage) return;
    const payload: PersistedSelection = {
      device_id: opt.device_id,
      friendly: opt.friendly,
      label: opt.label,
      facing: opt.facing,
    };
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Persistence is best-effort (private mode / quota); never throw.
    }
  }

  private persist_clear(): void {
    if (!this.storage) return;
    try {
      this.storage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  // ---- Snapshot ----------------------------------------------------------

  private build_snapshot(): CameraSelectorSnapshot {
    return {
      visible: this.visible,
      status: this.status,
      error: this.error,
      notice: this.notice,
      cameras: this.cameras.slice(),
      selected_device_id: this.selected_device_id,
      device_model: this.device_model,
      device_model_note: this.device_model_note,
    };
  }

  private commit(): void {
    this.snapshot = this.build_snapshot();
    for (const listener of this.listeners) listener();
  }
}
